/* eslint-disable no-unused-vars */
import React from 'react'

import { ReactiveVar } from '@apollo/client'

export type ReactiveVarState<T extends object> = {
  [K in keyof T]: ReactiveVar<T[K]>
}
export type RouteType = boolean | 'newWindow'

export type ClickHandler<E = undefined> = (e?: React.MouseEvent<HTMLElement> | E) => void

export interface AdditionalModalProps<E = Element> {
  label?: {
    confirm?: string
    cancel?: string
  }
  callback?: {
    cancel?: ClickHandler
    confirm?: ClickHandler
  }
  clickCodes?: {
    cancel?: string
    confirm?: string
  }
  rank?: number | string
  title?: string | React.ReactNode
  message?: string | React.ReactNode
  subMessage?: string | React.ReactNode
  hasLoading?: boolean
  hasUnderline?: boolean
  hasSubMessageScroll?: boolean
  children?: React.ReactNode
  className?: string
  isSequentialModal?: boolean
}

export interface ModalProps<E = Element> extends AdditionalModalProps<E> {
  showModal: boolean
  confirmModal: ClickHandler
  cancelModal: ClickHandler
}

export type PHASE = 'production' | 'stage' | 'qa' | 'development' | 'beta' | 'beta1'
export type ValueOf<T> = T[keyof T]
export type Nullable<T> = T | null
export type DeepNonNullable<T> = T extends object
  ? { [P in keyof T]-?: DeepNonNullable<NonNullable<T[P]>> }
  : NonNullable<T>

export type ArrayElement<array extends unknown[]> = array extends Array<infer E> ? E : never
export type ArrayOrValue<T> = T | T[]
export type DeepPartial<Thing> = Thing extends Function | Date
  ? Thing
  : Thing extends Array<infer InferredArrayMember>
  ? DeepPartialArray<InferredArrayMember>
  : Thing extends object
  ? DeepPartialObject<Thing>
  : Thing | undefined

type DeepPartialArray<Thing> = Array<DeepPartial<Thing>>

type DeepPartialObject<Thing> = {
  [Key in keyof Thing]?: DeepPartial<Thing[Key]>
}

export type ClickEvent = React.MouseEvent<HTMLElement> & {
  axesEvent?: any
  nativeEvent?: any
}

export type ChangeEvent = React.FormEvent<HTMLElement> & {
  axesEvent?: any
  nativeEvent?: any
}

export type Nullish<T> = T | null | undefined

export type ExcludeQueryParam<URL extends string> = URL extends `${infer Path}?${infer Query}` ? Path : URL

export type PathParam<Path extends string> = Path extends `${'http://' | 'https://'}${infer R}`
  ? PathParam<R>
  : // split path into individual path segments
  Path extends `${infer L}/${infer R}`
  ? PathParam<L> | PathParam<R>
  : // find params after `:`
  Path extends `${string}:${infer Param}`
  ? ExcludeQueryParam<Param>
  : // otherwise, there aren't any params present
    never

export type QueryParam<Path extends string> = Path extends `${infer _}${'?' | '&'}${infer Query}=${infer Rest}`
  ? Query | QueryParam<Rest>
  : never

/** 쿼리데이터에서 특정 typename 에 해당하는 document 타입 추출
 * ex. type VisitSucceedDocument = ExtractDocumentType< VisitQuery['Visit'], 'Visit_Succeed'> */
export type ExtractDocumentType<T, Typename extends T[keyof T]> = T extends { __typename?: Typename } ? T : never

export type StringParams<T extends string> = T extends `${infer L}{${infer P}}${infer R}`
  ? P | StringParams<R> | StringParams<L>
  : never

// https://github.com/thebuilder/react-intersection-observer#intersectionobserver-v2-
export interface ExtendIntersectionObserverEntry extends IntersectionObserverEntry {
  isVisible?: boolean
}

export enum ReviewDetailType {
  REVIEW_END,
  REVIEW_FEED,
}

type Fragment = { __typename: string }
export type ExcludeFragment<T extends Fragment, E extends Fragment> = T extends { __typename: E['__typename'] }
  ? never
  : T
